import { RestError } from '@azure/core-http';
import { Container, Grid, useMediaQuery, useTheme } from '@mui/material';
import { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { createPath, Paths, ProjectPathParams } from '../../../app/router/app.router';
import { WindowTitle } from '../../../core/window-title/window-title.component';
import { ErrorFullPage } from '../../../ui/components/pure/errors/error-full-page/error-full-page.component';
import { LoadingIndicator } from '../../../ui/components/pure/loading-indicator/loading-indicator.component';
import { CarrierDetailsPageHeader } from '../../../ui/components/pure/page-headers/details/carrier-details/carrier-details-page-header.component';
import { NotesCard } from '../../../ui/components/pure/notes-card/notes-card.component';
import { CarrierCouriersList } from './components/carrier-couriers-list.component';
import { useCarrierDetails } from './hooks/use-carrier-details.hook';
import { useCarrierNotes } from './hooks/use-carrier-notes.hook';
import { CarrierDetailsActions } from './actions/carrier-details.actions';
import { CarrierDetailsActionsObserver } from './carrier-details-actions-observer.component';
import { makeCarrierDetailsHeaderProps } from './logic/carrier-details-props.map';

export function CarrierDetailsPage({ params }: { params: ProjectPathParams<Paths.carrier> }) {
  const { carrier, isLoading, couriers, infiniteScroll, formState, onFormInput } = useCarrierDetails(
    params.carrierID,
    params.projectID
  );
  const notes = useCarrierNotes(params.carrierID);

  const [currentAction, setCurrentAction] = useState<CarrierDetailsActions | null>(null);
  const setAction = useCallback((action: CarrierDetailsActions | null) => setCurrentAction(action), []);

  const history = useHistory();
  const onDeleted = useCallback(
    () => history.push(createPath(Paths.carriers, { projectID: params.shortcode || params.projectID })),
    [history, params.shortcode, params.projectID]
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (carrier.error) {
    const status = (carrier.error as RestError).statusCode;

    if (status === 401) {
      return <ErrorFullPage preset="GLOBAL_ACCESS_DENIED" />;
    }

    if (status === 403) {
      return <ErrorFullPage preset="ACCESS_DENIED" />;
    }

    if (status === 404) {
      return <ErrorFullPage preset="NOT_FOUND" />;
    }

    return <ErrorFullPage />;
  }

  if (isLoading) {
    return <LoadingIndicator fullPage />;
  }

  if (!carrier.data) {
    return <ErrorFullPage preset="NOT_FOUND" />;
  }

  const headerProps = makeCarrierDetailsHeaderProps(carrier.data, params.projectID, setAction, params.shortcode);

  return (
    <Container>
      <WindowTitle detail={carrier.data.name} pageTitle="Carriers" />
      <CarrierDetailsPageHeader {...headerProps} />
      <CarrierDetailsActionsObserver
        actions={{ currentAction, setAction }}
        carrier={carrier.data}
        projectID={params.projectID}
        onDeleted={onDeleted}
      />
      {/* Reversed on mobile so the notes are not stranded after an infinitely scrolling table. */}
      <Grid
        container
        sx={{ flexDirection: isMobile ? 'column-reverse' : 'row' }}
        spacing={1}
        style={{ display: 'flex' }}
      >
        <Grid
          size={{
            xs: 12,
            lg: 8
          }}
        >
          <CarrierCouriersList
            params={params}
            pages={couriers.data?.pages || []}
            infiniteScroll={infiniteScroll}
            formState={formState}
            onFormInput={onFormInput}
            isLoading={couriers.isLoading}
            isPreviousData={couriers.isPreviousData}
            hasError={couriers.isError}
            inheritedCapabilities={carrier.data.capabilities || []}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            lg: 4
          }}
        >
          <NotesCard
            notes={notes.notes}
            hasError={notes.hasError}
            isLoading={notes.isLoading}
            hasNextPage={notes.hasNextPage}
            onClick={notes.onLoadMore}
            onAddNote={() => setAction(CarrierDetailsActions.ADD_NOTE)}
          />
        </Grid>
      </Grid>
    </Container>
  );
}
